import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Loader2, Hourglass, ShieldCheck, ShieldOff, Trash2, Play, Eye,
  AlertTriangle, CheckCircle, Clock, RefreshCw, History, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const PHASE_META = {
  monitoring: { label: 'Monitoring', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: Eye },
  warning_30: { label: 'Day 30', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Clock },
  warning_45: { label: 'Day 45', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  warning_53: { label: 'Day 53', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', icon: AlertTriangle },
  warning_57: { label: 'Day 57', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: AlertTriangle },
  purge_eligible: { label: 'Purge Eligible', color: 'bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-200', icon: Trash2 },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const StatCard = ({ label, value, icon: Icon, color = 'text-muted-foreground', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4 pb-3 px-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className={`h-6 w-6 ${color}`} />
      </div>
    </CardContent>
  </Card>
);

export const AdminCleanupQueue = () => {
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState('active');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [exemptDialog, setExemptDialog] = useState(null);
  const [exemptReason, setExemptReason] = useState('');
  const [purgeDialog, setPurgeDialog] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [detailTenant, setDetailTenant] = useState(null);
  const [tenantLogs, setTenantLogs] = useState([]);
  const [tenantLogsLoading, setTenantLogsLoading] = useState(false);

  const PAGE_SIZE = 25;

  const loadStats = useCallback(async () => {
    try { const r = await adminAPI.getCleanupStats(); setStats(r.data); } catch {}
  }, []);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, show: showFilter };
      if (phaseFilter !== 'all') params.phase = phaseFilter;
      const r = await adminAPI.getCleanupTenants(params);
      setTenants(r.data.tenants);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
    } catch { toast.error('Failed to load queue'); }
    setLoading(false);
  }, [page, showFilter, phaseFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleRunCheck = async (dryRun) => {
    setRunning(true);
    try {
      const r = await adminAPI.runCleanupCheck(dryRun);
      const s = r.data.stats;
      toast.success(`${dryRun ? 'Dry run' : 'Live check'}: ${s.tenants_evaluated} evaluated, ${s.newly_tracked} new, ${s.phase_advanced} advanced`);
      loadStats();
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Check failed'); }
    setRunning(false);
  };

  const handleExempt = async () => {
    if (!exemptReason.trim()) { toast.error('Reason required'); return; }
    try {
      await adminAPI.exemptTenant(exemptDialog.tenant_id, exemptReason);
      toast.success(`${exemptDialog.tenant_name} exempted`);
      setExemptDialog(null);
      setExemptReason('');
      loadStats();
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveExemption = async (t) => {
    try {
      await adminAPI.removeExemption(t.tenant_id);
      toast.success(`Exemption removed for ${t.tenant_name}`);
      loadStats();
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleApprovePurge = async () => {
    try {
      const r = await adminAPI.approvePurge(purgeDialog.tenant_id);
      if (r.data.error) { toast.error(r.data.error); return; }
      toast.success(`${purgeDialog.tenant_name} purged successfully`);
      setPurgeDialog(null);
      loadStats();
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Purge failed'); }
  };

  const handleViewLogs = async () => {
    setLogsOpen(true);
    setLogsLoading(true);
    try {
      const r = await adminAPI.getCleanupLogs({ limit: 50 });
      setLogs(r.data.logs);
    } catch { setLogs([]); }
    setLogsLoading(false);
  };

  const openDetail = async (t) => {
    setDetailTenant(t);
    setTenantLogsLoading(true);
    try {
      const r = await adminAPI.getCleanupLogs({ tenant_id: t.tenant_id, limit: 20 });
      setTenantLogs(r.data.logs);
    } catch { setTenantLogs([]); }
    setTenantLogsLoading(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-cleanup-queue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight" data-testid="cleanup-page-title">Cleanup Queue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Automated 60-day unused tenant lifecycle (dry run mode)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isPrimaryAdmin && <Button variant="outline" size="sm" onClick={() => handleRunCheck(true)} disabled={running} data-testid="run-dry-check-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
            Run Check (Dry)
          </Button>}
          {isPrimaryAdmin && <Button variant="outline" size="sm" onClick={() => handleRunCheck(false)} disabled={running} data-testid="run-live-check-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Run Check (Live)
          </Button>}
          <Button variant="outline" size="sm" onClick={handleViewLogs} data-testid="view-logs-btn">
            <History className="w-4 h-4 mr-1.5" /> Logs
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="cleanup-stats-grid">
          <StatCard label="Total Tracked" value={stats.total_tracked} icon={Hourglass} color="text-blue-500" testId="stat-tracked" />
          <StatCard label="Purge Awaiting Approval" value={stats.purge_awaiting_approval} icon={AlertTriangle} color="text-red-500" testId="stat-purge-eligible" />
          <StatCard label="Admin Exempted" value={stats.admin_exempted} icon={ShieldCheck} color="text-emerald-500" testId="stat-exempted" />
          <StatCard label="Has Activity" value={stats.has_activity} icon={CheckCircle} color="text-violet-500" testId="stat-has-activity" />
        </div>
      )}

      {stats && (
        <Card className="mb-6">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Phase Breakdown</p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.by_phase || {}).map(([phase, count]) => {
                const meta = PHASE_META[phase] || { label: phase, color: '' };
                return (
                  <div key={phase} className="flex items-center gap-1.5">
                    <Badge variant="secondary" className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                    <span className="text-sm font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={showFilter} onValueChange={(v) => { setShowFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="show-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Queue</SelectItem>
            <SelectItem value="exempt">Exempted</SelectItem>
            <SelectItem value="has_activity">Has Activity</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={phaseFilter} onValueChange={(v) => { setPhaseFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="phase-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Phases</SelectItem>
            {Object.entries(PHASE_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Queue Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cleanup-queue-table">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Tenant</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Owner</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Age</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Phase</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Last Check</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">
                  {showFilter === 'active' ? 'No tenants in the cleanup queue. Run a check to populate.' : 'No tenants match this filter.'}
                </td></tr>
              ) : tenants.map((t) => {
                const meta = PHASE_META[t.current_phase] || { label: t.current_phase, color: '', icon: Clock };
                const PIcon = meta.icon;
                return (
                  <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`queue-row-${t.tenant_id}`} onClick={() => openDetail(t)}>
                    <td className="px-4 py-3 font-medium truncate max-w-[180px]">{t.tenant_name}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell truncate max-w-[180px]">{t.owner_email}</td>
                    <td className="px-4 py-3">{t.age_days}d</td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className={`text-xs ${meta.color}`}>
                        <PIcon className="w-3 h-3 mr-1" />{meta.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {t.admin_exempted ? (
                        <Badge variant="outline" className="text-xs text-emerald-600"><ShieldCheck className="w-3 h-3 mr-1" />Exempted</Badge>
                      ) : t.has_activity ? (
                        <Badge variant="outline" className="text-xs text-violet-600"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">In queue</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell">{formatDateTime(t.last_activity_check_at)}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {isPrimaryAdmin && <div className="flex gap-1 justify-end">
                        {!t.admin_exempted && !t.has_activity && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setExemptDialog(t)} data-testid={`exempt-btn-${t.tenant_id}`}>
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" />Exempt
                          </Button>
                        )}
                        {t.admin_exempted && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleRemoveExemption(t)} data-testid={`remove-exempt-btn-${t.tenant_id}`}>
                            <ShieldOff className="w-3.5 h-3.5 mr-1" />Un-exempt
                          </Button>
                        )}
                        {t.current_phase === 'purge_eligible' && !t.admin_exempted && !t.has_activity && (
                          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => setPurgeDialog(t)} data-testid={`purge-btn-${t.tenant_id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />Purge
                          </Button>
                        )}
                      </div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} total)</p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Exempt Dialog */}
      <Dialog open={!!exemptDialog} onOpenChange={() => setExemptDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="exempt-dialog">
          <DialogHeader>
            <DialogTitle>Exempt Tenant</DialogTitle>
            <DialogDescription>
              Exempt <strong>{exemptDialog?.tenant_name}</strong> from the cleanup flow. The tenant will remain until you remove the exemption.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason for exemption..." value={exemptReason} onChange={(e) => setExemptReason(e.target.value)} data-testid="exempt-reason-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExemptDialog(null)}>Cancel</Button>
            <Button onClick={handleExempt} data-testid="exempt-confirm-btn"><ShieldCheck className="w-4 h-4 mr-1" />Exempt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge Dialog */}
      <Dialog open={!!purgeDialog} onOpenChange={() => setPurgeDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />Approve Purge</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{purgeDialog?.tenant_name}</strong> ({purgeDialog?.owner_email}). The owner's marketing contact will be preserved. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPurgeDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleApprovePurge} data-testid="purge-confirm-btn"><Trash2 className="w-4 h-4 mr-1" />Approve Purge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="logs-dialog">
          <DialogHeader>
            <DialogTitle>Cleanup Logs</DialogTitle>
          </DialogHeader>
          {logsLoading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No logs yet. Run a check to generate entries.</p>
          ) : (
            <div className="space-y-2" data-testid="cleanup-logs-list">
              {logs.map((l) => (
                <div key={l.id} className="border-l-2 border-muted pl-3 py-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{l.action.replace(/_/g, ' ')}</span>
                    {l.old_phase && l.new_phase && (
                      <span className="text-muted-foreground">{l.old_phase} &rarr; {l.new_phase}</span>
                    )}
                  </div>
                  <p className="text-muted-foreground">{l.details}</p>
                  <p className="text-muted-foreground">{formatDateTime(l.created_at)} &middot; {l.actor}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Tenant Detail Dialog */}
      <Dialog open={!!detailTenant} onOpenChange={() => setDetailTenant(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tenant-detail-dialog">
          {detailTenant && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">{detailTenant.tenant_name}</DialogTitle>
                <DialogDescription className="space-y-0.5">
                  <span className="block font-mono text-xs select-all">{detailTenant.tenant_id}</span>
                  <span className="block">{detailTenant.owner_email}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Phase + Age */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Current Phase</p>
                    <Badge variant="secondary" className={`text-xs mt-1 ${(PHASE_META[detailTenant.current_phase] || {}).color || ''}`}>
                      {(PHASE_META[detailTenant.current_phase] || {}).label || detailTenant.current_phase}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Account Age</p>
                    <p className="text-sm font-semibold mt-1">{detailTenant.age_days} days</p>
                  </div>
                </div>

                {/* Phase explanation */}
                {detailTenant.phase_detail && (
                  <div className="bg-muted/50 rounded-md px-3 py-2">
                    <p className="text-xs text-muted-foreground">{detailTenant.phase_detail}</p>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Created</span><p className="font-medium">{formatDate(detailTenant.created_at)}</p></div>
                  <div><span className="text-muted-foreground">Billing</span><p className="font-medium">{detailTenant.billing_status || 'trial'} / {detailTenant.billing_mode || 'stripe'}</p></div>
                  <div><span className="text-muted-foreground">Phase entered</span><p className="font-medium">{formatDateTime(detailTenant.phase_entered_at)}</p></div>
                  <div><span className="text-muted-foreground">Last check</span><p className="font-medium">{formatDateTime(detailTenant.last_activity_check_at)}</p></div>
                  {detailTenant.is_beta && <div><span className="text-muted-foreground">Beta</span><p className="font-medium">Yes</p></div>}
                </div>

                {/* Status Reasons */}
                {detailTenant.status_reasons && detailTenant.status_reasons.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Status / Protection Reasons</p>
                    <div className="space-y-1.5">
                      {detailTenant.status_reasons.map((r, i) => {
                        const colors = {
                          admin_exempted: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20',
                          has_activity: 'border-violet-300 bg-violet-50 dark:bg-violet-900/20',
                          paid_tenant: 'border-blue-300 bg-blue-50 dark:bg-blue-900/20',
                          active_subscription: 'border-blue-300 bg-blue-50 dark:bg-blue-900/20',
                          comped: 'border-amber-300 bg-amber-50 dark:bg-amber-900/20',
                          too_young: 'border-slate-300 bg-slate-50 dark:bg-slate-800/40',
                        };
                        const icons = {
                          admin_exempted: ShieldCheck,
                          has_activity: CheckCircle,
                          paid_tenant: CheckCircle,
                          active_subscription: CheckCircle,
                          comped: ShieldCheck,
                          too_young: Clock,
                        };
                        const RIcon = icons[r.type] || AlertTriangle;
                        return (
                          <div key={i} className={`border-l-2 pl-3 py-1.5 rounded-r-sm ${colors[r.type] || 'border-muted'}`}>
                            <div className="flex items-center gap-1.5 text-xs">
                              <RIcon className="w-3.5 h-3.5 shrink-0" />
                              <span className="font-medium">{r.label}</span>
                            </div>
                            {r.detail && <p className="text-[11px] text-muted-foreground mt-0.5 ml-5">{r.detail}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Exemption details */}
                {detailTenant.admin_exempted && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md px-3 py-2">
                    <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">Exempted by {detailTenant.exempted_by}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">{formatDateTime(detailTenant.exempted_at)}</p>
                    {detailTenant.exemption_reason && <p className="text-xs mt-1">{detailTenant.exemption_reason}</p>}
                  </div>
                )}

                {/* Activity details */}
                {detailTenant.has_activity && (
                  <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-md px-3 py-2">
                    <p className="text-xs font-medium text-violet-800 dark:text-violet-300">Activity detected {formatDateTime(detailTenant.activity_detected_at)}</p>
                    {detailTenant.activity_reason && <p className="text-xs mt-1">{detailTenant.activity_reason}</p>}
                  </div>
                )}

                {/* Actions */}
                {isPrimaryAdmin && <div className="flex gap-2 flex-wrap pt-1">
                  {!detailTenant.admin_exempted && !detailTenant.has_activity && (
                    <Button variant="outline" size="sm" onClick={() => { setDetailTenant(null); setExemptDialog(detailTenant); }} data-testid="detail-exempt-btn">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Exempt
                    </Button>
                  )}
                  {detailTenant.admin_exempted && (
                    <Button variant="outline" size="sm" onClick={async () => { await handleRemoveExemption(detailTenant); setDetailTenant(null); }} data-testid="detail-unexempt-btn">
                      <ShieldOff className="w-3.5 h-3.5 mr-1" /> Remove Exemption
                    </Button>
                  )}
                  {detailTenant.current_phase === 'purge_eligible' && !detailTenant.admin_exempted && !detailTenant.has_activity && (
                    <Button variant="destructive" size="sm" onClick={() => { setDetailTenant(null); setPurgeDialog(detailTenant); }} data-testid="detail-purge-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Approve Purge
                    </Button>
                  )}
                </div>}

                {/* Tenant-specific logs */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Lifecycle History</p>
                  {tenantLogsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : tenantLogs.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No log entries</p>
                  ) : (
                    <div className="space-y-1.5 max-h-36 overflow-y-auto" data-testid="tenant-lifecycle-logs">
                      {tenantLogs.map((l) => (
                        <div key={l.id} className="border-l-2 border-muted pl-3 py-1 text-xs">
                          <span className="font-medium">{l.action.replace(/_/g, ' ')}</span>
                          {l.old_phase && l.new_phase && <span className="text-muted-foreground ml-1">{l.old_phase} &rarr; {l.new_phase}</span>}
                          {l.details && <p className="text-muted-foreground">{l.details}</p>}
                          <p className="text-muted-foreground">{formatDateTime(l.created_at)} &middot; {l.actor}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCleanupQueue;
